import {
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { assets } from '../../../core/constants/assets.const';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatCheckboxChange,
  MatCheckboxModule
} from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { SignupComponent } from '../signup/signup.component';
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';
import { RouterModule } from '@angular/router';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { BaseClass } from '../../../shared/baseClass/baseClass';
import { emailErrors } from '../../../core/constants/commonErrors.const';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { countryCode } from '../../../models/countryCode.model';
import { CountryCodeConst } from '../../../core/constants/countryCode.const';
import { OtpType, SignUpType } from '../../../core/constants/keys.constant';
import { environment } from '../../../../environments/environment';
import {
  guestLoginData,
  socialLoginPaylaod,
  User,
  UserLogin
} from '../../../models/auth.model';
import { responseStatus } from '../../../core/constants/responseStatus.const';
import { googleAccountResponse } from '../../../models/socialLogin.model';
import { socialLoginService } from '../../../services/socialLogin.service';
import { VerificationComponent } from '../verification/verification.component';
import { profileSetupStatus } from '../../../core/constants/booking.const';
import { ProfileSetupComponent } from '../profile-setup/profile-setup.component';
import { AnalyticsService } from '../../../services/analytics.service';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const google: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const FB: any;

@Component({
  selector: 'xplora-website-login',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDialogModule,
    TranslateModule,
    MatCheckboxModule,
    RouterModule,
    MatTabsModule,
    NgxMatIntlTelInputComponent,
    ReactiveFormsModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent extends BaseClass implements OnInit, OnDestroy {
  protected countryCodeObject: countryCode = CountryCodeConst;
  protected loginFormGroup: FormGroup;
  protected assetsConst = assets;
  protected emailErrors = emailErrors;
  protected hide = true;
  private destroy: Subject<boolean> = new Subject<boolean>();
  private deviceToken!: string;
  protected submitted = false;
  protected selectedTab = 0;
  protected loginType = SignUpType.Email;
  protected isRemembered = false;
  protected guestId: string | null = null;
  protected profileSetupStatus = profileSetupStatus;

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<LoginComponent>,
    private _authService: AuthService,
    private _socialLoginService: socialLoginService,
    private gs: AnalyticsService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(MAT_DIALOG_DATA) private data?: guestLoginData
  ) {
    super(injector);

    this.loginFormGroup = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      phoneNo: [{ value: '', disabled: true }, [Validators.required]],
      dialCode: [{ value: '', disabled: true }]
    });

    this._authService.deviceTokenSubject
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: string) => {
          this.deviceToken = res;
        }
      });

    if (this.data) {
      this.guestId = this.data.guestId??null;
    }

    if (isPlatformBrowser(this.platformId)) {
      if (!localStorage.getItem(environment.storageKey)) {
        this.guestId = localStorage.getItem(environment.guestId);
      }
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (isPlatformBrowser(this.platformId)) {
        const rememberMeData = localStorage.getItem(environment.rememberMeKey);
        if (rememberMeData) {
          this.isRemembered = true;

          const body = JSON.parse(rememberMeData);

          const fullPhoneNumber =
            body.dialCode && body.phoneNo
              ? `${body.dialCode}${body.phoneNo}`
              : '';
          this.loginFormGroup.patchValue({
            email: body.email ? body.email : '',
            phoneNo: fullPhoneNumber,
            dialCode: body.dialCode ? body.dialCode : '',
            password: body.password
          });
        }
      }
      this.initializeGoogleLogin();
      this.initFacebookSDK();
    }
  }

  initFacebookSDK(): void {
    if (isPlatformBrowser(this.platformId)) {
      FB.init({
        appId: environment.facebookAppId,
        cookie: true,
        xfbml: true,
        version: 'v10.0'
      });
    }
  }

  initializeGoogleLogin(): void {
    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: this.handleGoogleCallback.bind(this)
    });

    google.accounts.id.renderButton(
      document.getElementById('googleSignInDiv'),
      { theme: 'outline', size: 'large' }
    );
  }

  handleGoogleCallback(response: googleAccountResponse): void {
    if (response && response.credential) {
      const token = response.credential;
      const tokenParts = token.split('.');

      if (tokenParts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      const payload = tokenParts[1];
      const decodedPayload = this.base64UrlDecode(payload);
      const userPayload: socialLoginPaylaod = {
        googleId: decodedPayload.sub,
        email: decodedPayload.email,
        firstName: decodedPayload.name,
        image: decodedPayload.picture,
        deviceType: 'WEB',
        deviceToken: this._authService.deviceTokenSubject.value
      };

      this.socialLoginApiHit(userPayload);
    }
  }

  handleFacebookLogin(): void {
    FB.login(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (response: any) => {
        if (response.authResponse) {
          FB.api('/me?fields=id,name,email,picture', (profile: any) => {
            const userPayload = {
              facebookId: profile.id,
              email: profile.email,
              firstName: profile.name,
              image: profile.picture?.data?.url,
              deviceType: 'WEB',
              deviceToken: this._authService.deviceTokenSubject.value
            };
            this.socialLoginApiHit(userPayload);
          });
        }
      },
      { scope: 'email' }
    );
  }

  socialLoginApiHit(userPayload: socialLoginPaylaod): void {
    this.spinnerService.addToLoader('userLogin');
    if (this.data?.trialFlow && this.data?.membershipPlan?.id) {
      (userPayload as any).membership_id = this.data.membershipPlan.id;
    }
    const query = this.guestId ? `guestId=${this.guestId}` : '';
    this._socialLoginService
      .userSocialLogin(userPayload, query)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: User) => {
          this.gs.pushToDataLayer({ event: 'login' });
          this.spinnerService.removeFromLoader('userLogin');

          if (isPlatformBrowser(this.platformId)) {
            localStorage.removeItem(environment.guestId);
            // alert(1)
            this._authService.updateMembershipStatus(res.membership || false);
            window.location.reload();
          }
          this.toasterService.successToastr(
            this.translateService.instant('LoginSuccessful')
          );
          if (isPlatformBrowser(this.platformId)) {
            // alert('2')
            localStorage.setItem(
              environment.storageKey,
              res.accessToken ? res.accessToken : ''
            );
            this._authService.updateMembershipStatus(res.membership || false);
            window.location.reload();
          }
          this._authService.profileDetails.next(res);
          this.dialogRef.close(responseStatus.SUCCESS);
        },
        error: () => {
          this.spinnerService.removeFromLoader('userLogin');
        }
      });
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.submitted = false;
    this.selectedTab = event.index;
    if (this.selectedTab === 0) {
      this.loginType = SignUpType.Email;
      this.loginFormGroup.get('password')?.enable();
      this.loginFormGroup.get('email')?.enable();
      this.loginFormGroup.get('phoneNo')?.disable();
      this.loginFormGroup.get('dialCode')?.disable();
      this.loginFormGroup.get('email')?.updateValueAndValidity();
      this.loginFormGroup.get('phoneNo')?.updateValueAndValidity();
      this.loginFormGroup.get('dialCode')?.updateValueAndValidity();
    } else {
      this.loginType = SignUpType.Phone;
      this.loginFormGroup.get('password')?.disable();
      this.loginFormGroup.get('email')?.disable();
      this.loginFormGroup.get('phoneNo')?.enable();
      this.loginFormGroup.get('dialCode')?.enable();
      this.loginFormGroup.get('email')?.updateValueAndValidity();
      this.loginFormGroup.get('phoneNo')?.updateValueAndValidity();
      this.loginFormGroup.get('dialCode')?.updateValueAndValidity();
    }
  }

  toggleRememberMe(event: MatCheckboxChange): void {
    this.isRemembered = event['checked'];
  }

  openSignupModal(): void {
    const dialogRef = this.dialog.open(SignupComponent, {
      panelClass: ['modal', 'auth_modal']
    });
    dialogRef.afterClosed().subscribe(() => {});
  }

  openForgotPasswordModal(): void {
    const dialogRef = this.dialog.open(ForgotPasswordComponent, {
      panelClass: ['modal', 'auth_modal']
    });
    dialogRef.afterClosed().subscribe(() => {});
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countryChangedEvent(event: any): void {
    this.countryCodeObject = event;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.loginFormGroup.valid) {
      const modifiedValue = this.patchValues();
      if (this.loginType === SignUpType.Email) {
        modifiedValue.deviceType = 'WEB';
        if (isPlatformBrowser(this.platformId)) {
          modifiedValue.deviceToken =
            localStorage.getItem('fcmToken') || 'xxxxxxxxxx';
        }
        this.loginApiHit(modifiedValue);
      } else {
        this.loginApiHit(modifiedValue);
      }
      this.gs.pushToDataLayer({ event: 'login' });
    }
  }

  loginApiHit(payload: UserLogin): void {
    this.spinnerService.addToLoader('userLogin');
    if (this.data?.trialFlow && this.data?.membershipPlan?.id) {
      (payload as any).membership_id = this.data.membershipPlan.id;
    }
    const query = this.guestId ? `guestId=${this.guestId}` : '';
    this._authService.userLogin(payload, query)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: User) => {
          if (res.noOfCartItems) {
            this._authService.cartCount.next(res.noOfCartItems);
          }
          this.spinnerService.removeFromLoader('userLogin');
          if (isPlatformBrowser(this.platformId)) {
            // alert('3')
            localStorage.removeItem(environment.guestId);
            this._authService.updateMembershipStatus(res.membership || false);
            // window.location.reload();
          }
          if (this.loginType === SignUpType.Email) {
            this.handleAfterLoginSuccess(payload, res);
          } else {
            const dialogRef = this.dialog.open(VerificationComponent, {
              panelClass: ['modal', 'auth_modal'],
              data: {
                signUpType: SignUpType.Phone,
                otpType: OtpType.login,
                data: payload
              }
            });
            dialogRef
              .afterClosed()
              .pipe(takeUntil(this.destroy))
              .subscribe((res: User) => {
                if (res) {
                  this.handleAfterLoginSuccess(payload, res);
                }
              });
          }
        },
        error: () => {
          this.spinnerService.removeFromLoader('userLogin');
        }
      });
  }

  handleAfterLoginSuccess(modifiedValue: UserLogin, res: User): void {
    this.dialogRef.close();
    this.spinnerService.removeFromLoader('userLogin');
    
    // Check if this is a trial flow and show appropriate message
    if (this.data?.trialFlow && (res as any).message) {
      const message = (res as any).message;
      if (message.includes('already used trial-membership')) {
        this.toasterService.infoToastr('You have already used your 14-day trial membership.');
      } else {
        this.toasterService.successToastr(message);
      }
    } else {
      this.toasterService.successToastr(
        this.translateService.instant('LoginSuccessful')
      );
    }
    if (isPlatformBrowser(this.platformId)) {
    // alert('4')  
      localStorage.setItem(
        environment.storageKey,
        res.accessToken ? res.accessToken : ''
      );
      this._authService.updateMembershipStatus(res.membership || false);
      window.location.reload();
    }
    if (res.profileStepsCompleted === this.profileSetupStatus.signup) {
      const data = {
        data: res,
        profileSetup: true,
        signUpType: this.loginType
      };

      this.dialog.open(ProfileSetupComponent, {
        panelClass: ['modal', 'auth_modal', 'setup_modal'],
        data
      });
    } else {
      this._authService.profileDetails.next(res);
      if (this.isRemembered) {
        const data: UserLogin = {
          [this.loginType === SignUpType.Email ? 'email' : 'phoneNo']:
            this.loginType === SignUpType.Email
              ? modifiedValue['email']
              : modifiedValue['phoneNo'],
          password: this.loginFormGroup['controls']['password']['value']
        };

        if (this.loginType === SignUpType.Phone) {
          data.dialCode = modifiedValue.dialCode;
        }

        if (isPlatformBrowser(this.platformId)) {
          // alert('5')
          localStorage.setItem(environment.rememberMeKey, JSON.stringify(data));
          this._authService.updateMembershipStatus(res.membership || false);
          window.location.reload();
        }
      } else {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.removeItem(environment.rememberMeKey);
          //  localStorage.removeItem('membershipStatus');
        }
      }
      this.dialogRef.close(responseStatus.SUCCESS);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patchValues(): any {
    // eslint-disable-next-line prefer-const, @typescript-eslint/no-explicit-any
    let value: any;
    const patchValueToCountryCode = (): void => {
      let tempCountryCode = this.loginFormGroup.get('dialCode')?.value;
      if (tempCountryCode) {
        tempCountryCode = tempCountryCode.split('');
        tempCountryCode.shift();
        tempCountryCode = tempCountryCode.join('');
      }
      if (tempCountryCode !== this.countryCodeObject.dialCode) {
        this.loginFormGroup.patchValue({
          dialCode: `+${this.countryCodeObject.dialCode}`
        });
      }
    };
    const patchValueToPhoneField = (): void => {
      let phone = this.loginFormGroup.get('phoneNo')?.value;
      if (phone && phone.includes(this.loginFormGroup.get('dialCode')?.value)) {
        phone = phone.split(this.loginFormGroup.get('dialCode')?.value);
        if (value.phoneNo !== undefined) {
          value.phoneNo = phone[1].trim();
        }
      }
    };
    patchValueToCountryCode();
    value = this.loginFormGroup['value'];
    patchValueToPhoneField();
    return value;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  base64UrlDecode(base64Url: string): any {
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64);
    return JSON.parse(decoded);
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
